import { useEffect, useState } from "react";
import { AlertCircle, Loader2, Mail, ShieldCheck } from "lucide-react";
import { cn } from "./ui";
import { apiErrorText } from "@/lib/api";
import { t } from "@/lib/i18n";
import { useAuth } from "./auth";
import { channelsApi, type Channel, type EmailApp } from "./inbox-api";
import { Hint, SideCard, Steps, inputCls, type PanelProps } from "./channel-wizard-kit";

/* Почта: ящик сотрудника Gmail Workspace подключается по адресу — без паролей и кодов, через сервисный аккаунт Google
   с делегированием домена (ключ задан на сервере один раз). Продавец выбирает свой адрес, Hub проверяет доступ и начинает
   подтягивать переписку с клиентами. Переподключение (existing) — та же проверка и новый запуск синхронизации. */

export default function EmailLogin({ onDone, existing }: PanelProps & { existing?: Channel | null }) {
  const { user } = useAuth();
  const [app, setApp] = useState<EmailApp | null>(null);
  const [address, setAddress] = useState(existing?.name ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    channelsApi
      .emailApp()
      .then((a) => {
        setApp(a);
        if (!existing && !address) {
          const mine = a.users.find((x) => x.email.toLowerCase() === (user?.email ?? "").toLowerCase());
          setAddress(mine?.email ?? "");
        }
      })
      .catch((e) => setError(apiErrorText(e)));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const valid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(address.trim());

  const connect = async () => {
    if (!valid || busy) return;
    setBusy(true);
    setError("");
    try {
      const ch = existing ? await channelsApi.reconnect(existing.id) : await channelsApi.create("email", address.trim().toLowerCase());
      const who = app?.users.find((x) => x.id === ch.settings.userId);
      onDone(ch.name, [
        { label: t("Тип"), value: t("Почта · Gmail Workspace") },
        { label: t("Ящик"), value: ch.name },
        { label: t("Ответственный за письма"), value: who?.name ?? (ch.settings.userId ? "—" : t("не назначен: сотрудника с таким адресом нет в Hub")) },
        { label: t("Глубина истории"), value: t("{n} дней", { n: ch.settings.historyDays }) },
      ]);
    } catch (e) {
      setError(apiErrorText(e));
    } finally {
      setBusy(false);
    }
  };

  const listed = app?.users ?? [];
  const custom = address && !listed.some((x) => x.email === address);

  return (
    <div className="grid flex-1 overflow-y-auto lg:grid-cols-[minmax(0,1fr)_20rem]">
      <div className="min-w-0 p-4 md:p-5" data-testid="email-setup">
        <Steps active={app?.configured ? 2 : 1} items={[t("Ключ Google Workspace на сервере (один раз)"), t("Выбрать ящик сотрудника"), t("Готово")]} />
        {app && !app.configured ? (
          <div className="mt-4 rounded-btn border border-amber-400/50 bg-amber-400/10 p-3 text-[11.5px] leading-relaxed text-amber-700">
            {t("Почта ещё не настроена на сервере: нужен ключ сервисного аккаунта Google с делегированием домена (тот же, что читает почту для baza.starec.ai). Это делается один раз администратором сервера, дальше ящики подключаются по адресу.")}
          </div>
        ) : null}
        <div className="mt-4 rounded-btn border border-border bg-background p-3">
          <p className="text-[12.5px] font-semibold text-foreground">{t("Чей ящик подключаем")}</p>
          <p className="mt-1 text-[11.5px] leading-relaxed text-muted-foreground">
            {t("Выберите сотрудника — его письма с клиентами станут диалогами во «Входящих» и попадут в ленту карточки клиента рядом с WhatsApp. Внутренняя переписка между сотрудниками не переносится. Ответственный за письма — сам владелец ящика.")}
          </p>
          {listed.length > 0 && !existing ? (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {listed.map((x) => (
                <button
                  key={x.id}
                  type="button"
                  onClick={() => setAddress(x.email)}
                  aria-pressed={address === x.email}
                  className={cn(
                    "cursor-pointer rounded-full border px-2.5 py-1 text-[11.5px] font-medium transition",
                    address === x.email ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground",
                  )}
                >
                  {x.name} · {x.email}
                </button>
              ))}
            </div>
          ) : null}
          <label className="mt-3 block">
            <span className="text-[11px] font-semibold text-muted-foreground">{t("Адрес ящика")}</span>
            <input
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              disabled={Boolean(existing) || busy}
              placeholder={app?.domains[0] ? `name@${app.domains[0]}` : "name@company.com"}
              inputMode="email"
              autoComplete="off"
              className={cn(inputCls, "mt-1")}
              data-testid="email-address"
            />
          </label>
          {custom && app?.domains.length ? (
            <p className="mt-1 text-[11px] text-muted-foreground">{t("Сотрудника с таким адресом в Hub нет — письма будут видны администраторам, пока его не заведут в «Пользователях».")}</p>
          ) : null}
          {error ? (
            <p className="mt-2 flex items-start gap-1.5 text-[11.5px] text-red-500">
              <AlertCircle className="mt-0.5 size-3.5 flex-none" aria-hidden />
              {error}
            </p>
          ) : null}
          <button
            type="button"
            onClick={() => void connect()}
            disabled={!valid || busy || (app ? !app.configured : true)}
            className="mt-3 inline-flex h-10 cursor-pointer items-center gap-2 whitespace-nowrap rounded-btn bg-primary px-4 text-[12.5px] font-semibold text-primary-foreground transition hover:brightness-110 disabled:cursor-default disabled:opacity-50"
            data-testid="email-connect"
          >
            {busy ? <Loader2 className="size-4 animate-spin" aria-hidden /> : <Mail className="size-4" aria-hidden />}
            {existing ? t("Проверить доступ и обновить") : t("Подключить ящик")}
          </button>
        </div>
        <Hint icon={<ShieldCheck className="size-3.5" />}>
          {t("Пароли не нужны: доступ к ящикам домена выдан сервисному аккаунту Hub в Google Admin. Hub читает письма и отправляет ответы от имени ящика сотрудника — они видны и в самом Gmail в «Отправленных».")}
        </Hint>
      </div>
      <aside className="flex flex-col gap-3 border-t border-border bg-muted/30 p-4 lg:border-s lg:border-t-0">
        <SideCard title={t("Что попадёт в Hub")}>
          <ul className="mt-2 flex list-disc flex-col gap-1 pl-4 text-[11.5px] leading-relaxed text-muted-foreground">
            <li>{t("Письма клиентам и от клиентов за последний год, затем — новые каждые несколько минут.")}</li>
            <li>{t("Одна переписка с адресом = один диалог: входящие и отправленные вместе, как в WhatsApp.")}</li>
            <li>{t("Клиент узнаётся по адресу или по домену компании — письмо сразу видно в карточке клиента.")}</li>
            <li>{t("Рассылки, спам и внутренняя почта между сотрудниками не переносятся.")}</li>
          </ul>
        </SideCard>
        <SideCard title={t("Вложения")}>
          <p className="mt-2 text-[11.5px] leading-relaxed text-muted-foreground">{t("Файлы писем открываются из диалога по ссылке и не копируются на сервер Hub.")}</p>
        </SideCard>
      </aside>
    </div>
  );
}
